<template>
  <div class="table-wrapper" ref="tableWrap">
    <template v-if="showAlert">
      <a-alert show-icon style="margin-bottom: 16px">
        <template slot="message">
          <span style="margin-right: 12px">已选择: <a style="font-weight: 600">{{ selectedRows.length }}</a></span>
          <span v-for="item in needTotalList" :key="item.dataIndex" style="margin-right: 12px">
            {{ item.title }}总计 <a
            style="font-weight: 600">{{ item.customRender ? item.customRender(item.total) : item.total }}</a>
          </span>
          <component :is="clearRender"/>
        </template>
      </a-alert>
    </template>

    <a-table
      ref="table"
      v-bind="tableProps"
      :scroll="realScroll"
      table-layout="fixed"
      @change="loadData"
      :pagination="false"
    >
      <template v-for="slotName in slotNames" :slot="slotName" slot-scope="text, record, index, column">
        <slot :name="slotName" v-bind="{
          text, record, index, column }"/>
      </template>
    </a-table>
  </div>
</template>

<script>
import get from 'lodash.get'
import { Table } from 'ant-design-vue'

export default {
  name: 'StandardTable',
  props: Object.assign({}, Table.props, {
    rowKey: {
      type: [String, Function],
      default: 'key'
    },
    columns: {
      type: Array,
      default: () => []
    },
    data: {
      type: Function,
      required: true
    },
    pageNum: {
      type: Number,
      default: 1
    },
    pageSize: {
      type: Number,
      default: 10
    },
    showSizeChanger: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'default'
    },
    alert: {
      type: [Object, Boolean],
      default: null
    },
    rowSelection: {
      type: Object,
      default: null
    },
    showAlertInfo: {
      type: Boolean,
      default: false
    },
    showPagination: {
      type: [String, Boolean],
      default: 'auto'
    },
    pageURI: {
      type: Boolean,
      default: false
    },
    useScroll: {
      type: Boolean,
      default: false
    }
  }),
  data() {
    return {
      needTotalList: [],
      selectedRows: [],
      selectedRowKeys: [],
      localLoading: false,
      localDataSource: [],
      localPagination: this.validateAndCreatePagination(this.pagination),
      tableScrollY: 0,
      resizeObserver: null,
      resizeTimer: null,
      resizeLock: false,
      slotNames: []
    }
  },
  computed: {
    realScroll() {
      if (!this.useScroll) {
        return {
          x: 'max-content'
        }
      }
      // const originScroll = this.$attrs.scroll || {}
      return {
        y: this.tableScrollY,
        x: 'max-content'
      }
    },
    tableProps() {
      const props = {}
      const localKeys = Object.keys(this.$data)
      Object.keys(Table.props).forEach(k => {
        const localKey = `local${k.substring(0, 1).toUpperCase()}${k.substring(1)}`
        if (localKeys.includes(localKey)) {
          props[k] = this[localKey]
          return props[k]
        }
        if (k === 'rowSelection') {
          if (this.showAlert && this.rowSelection) {
            props[k] = {
              ...this.rowSelection,
              selectedRows: this.selectedRows || [],
              selectedRowKeys: this.selectedRowKeys || [],
              onChange: (selectedRowKeys, selectedRows) => {
                this.updateSelect(selectedRowKeys, selectedRows)
                typeof this.rowSelection.onChange === 'function' && this.rowSelection.onChange(selectedRowKeys, selectedRows)
              }
            }
            return props[k]
          } else if (!this.rowSelection) {
            props[k] = null
            return props[k]
          }
        }
        if (k === 'pagination') {
          props[k] = ['auto', true].includes(this.showPagination) ? this.localPagination : false
          return props[k]
        }
        this[k] && (props[k] = this[k])
      })
      props.size = this.size
      props.columns = this.columns
      props.loading = this.localLoading
      props.dataSource = this.localDataSource
      props.rowKey = this.rowKey
      props.childrenColumnName = this.childrenColumnName
      return props
    },
    showAlert() {
      return (typeof this.alert === 'object' && this.alert !== null && this.alert.show) && !!this.rowSelection
    }
  },
  watch: {
    // 增加immediate: true，初始化立即执行
    columns: {
      handler(val) {
        this.initSlotNames(val)
      },
      immediate: true
    },
    'localPagination.current'(val) {
      if (this.pageURI && this.$route) {
        this.$router.push({
          ...this.$route,
          name: this.$route.name,
          params: { ...this.$route.params, pageNo: val }
        })
      }
    },
    pageNum(val) {
      if (this.localPagination) {
        this.localPagination = this.validateAndCreatePagination({ ...this.localPagination, current: val })
      }
    },
    pageSize(val) {
      if (this.localPagination) {
        this.localPagination = this.validateAndCreatePagination({ ...this.localPagination, pageSize: val })
      }
    },
    showSizeChanger(val) {
      if (this.localPagination) {
        this.localPagination = this.validateAndCreatePagination({ ...this.localPagination, showSizeChanger: val })
      }
    }
  },
  created() {
    // 初始化分页
    const { pageNo } = this.$route.params
    const localPageNum = this.pageURI && pageNo ? parseInt(pageNo) : this.pageNum
    this.localPagination = ['auto', true].includes(this.showPagination)
      ? this.validateAndCreatePagination({
        current: localPageNum,
        pageSize: this.pageSize,
        showSizeChanger: this.showSizeChanger
      })
      : false
    this.needTotalList = this.initTotalList(this.columns)
    // 手动初始化插槽（兜底，防止watch失效）
    this.initSlotNames(this.columns)
    this.loadData()
  },
  mounted() {
    this.initResizeObserver()
  },
  beforeDestroy() {
    if (this.resizeTimer) clearTimeout(this.resizeTimer)
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },
  methods: {
    // 抽离插槽初始化方法，created/watch共用
    initSlotNames(val) {
      const headerSlots = val.filter(item => item.slots?.title).map(item => item.slots.title)
      const cellSlots = val.filter(item => item.scopedSlots?.customRender).map(item => item.scopedSlots.customRender)
      this.slotNames = [...new Set([...headerSlots, ...cellSlots])]
    },
    validateAndCreatePagination(pagination) {
      if (!pagination) return false
      if (typeof pagination === 'boolean') {
        return pagination ? {
          current: Number(this.pageNum) || 1,
          pageSize: Number(this.pageSize) || 10,
          showSizeChanger: this.showSizeChanger,
          total: 0
        } : false
      }
      if (typeof pagination === 'object') {
        return {
          current: Number(pagination.current) || Number(this.pageNum) || 1,
          pageSize: Number(pagination.pageSize) || Number(this.pageSize) || 10,
          showSizeChanger: pagination.showSizeChanger ?? this.showSizeChanger,
          total: Number(pagination.total) || 0,
          showQuickJumper: pagination.showQuickJumper ?? true,
          showTotal: pagination.showTotal || ((total, range) => `${range[0]}-${range[1]} of ${total} items`),
          ...pagination
        }
      }
      return {
        current: Number(this.pageNum) || 1,
        pageSize: Number(this.pageSize) || 10,
        showSizeChanger: this.showSizeChanger,
        total: 0
      }
    },
    initResizeObserver() {
      const wrapEl = this.$refs.tableWrap
      if (!wrapEl) return
      this.resizeObserver = new window.ResizeObserver(entries => {
        if (this.resizeLock) return
        if (!entries.length) return
        if (this.resizeTimer) clearTimeout(this.resizeTimer)
        this.resizeTimer = setTimeout(() => {
          this.resizeLock = true
          const wrapH = wrapEl.clientHeight
          const reserve = 56 + 8
          let scrollY = wrapH - reserve
          scrollY = Math.max(scrollY, 80)
          this.tableScrollY = scrollY
          setTimeout(() => this.resizeLock = false, 100)
        }, 80)
      })
      this.resizeObserver.observe(wrapEl)
    },
    refresh(bool = false) {
      if (bool && this.localPagination) {
        this.localPagination = this.validateAndCreatePagination({
          current: 1,
          pageSize: this.pageSize,
          showSizeChanger: this.showSizeChanger
        })
      }
      this.loadData()
    },
    loadData(pagination, filters, sorter) {
      this.localLoading = true
      const pageCache = this.localPagination || {
        current: Number(this.pageNum) || 1,
        pageSize: Number(this.pageSize) || 10
      }
      const parameter = Object.assign({
          pageNo: (pagination?.current) || (this.showPagination && pageCache.current) || Number(this.pageNum) || 1,
          pageSize: (pagination?.pageSize) || (this.showPagination && pageCache.pageSize) || Number(this.pageSize) || 10
        },
        sorter?.field ? { sortField: sorter.field } : {},
        sorter?.order ? { sortOrder: sorter.order } : {},
        { ...filters || {} }
      )
      const req = this.data(parameter)
      if (req && typeof req.then === 'function') {
        req.then(r => {
          this.localPagination = this.showPagination
            ? this.validateAndCreatePagination({
              current: r.pageNo,
              total: Number(r.totalCount) || 0,
              showSizeChanger: this.showSizeChanger,
              pageSize: pagination?.pageSize || pageCache.pageSize,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              ...this.pagination
            })
            : false
          if (r.data.length === 0 && this.showPagination && this.localPagination && this.localPagination.current > 1) {
            this.localPagination.current--
            this.loadData()
            return
          }
          try {
            if (['auto', true].includes(this.showPagination) && r.totalCount <= r.pageNo * pageCache.pageSize) {
              this.localPagination.hideOnSinglePage = true
            }
          } catch (e) {
            this.localPagination = false
          }
          this.localDataSource = r.data
          this.localLoading = false
        })
      }
    },
    initTotalList(columns) {
      const list = []
      columns?.forEach(col => {
        if (col.needTotal) list.push({ ...col, total: 0 })
      })
      return list
    },
    updateSelect(selectedRowKeys, selectedRows) {
      this.selectedRows = selectedRows || []
      this.selectedRowKeys = selectedRowKeys || []
      this.needTotalList = this.needTotalList.map(item => ({
        ...item,
        total: this.selectedRows.reduce((sum, val) => {
          const num = parseInt(get(val, item.dataIndex))
          return sum + (isNaN(num) ? 0 : num)
        }, 0)
      }))
    },
    clearSelected() {
      if (this.rowSelection) {
        this.selectedRowKeys = []
        this.selectedRows = []
        this.rowSelection.selectedRowKeys = []
        this.rowSelection.selectedRows = []
        this.rowSelection.onChange([], [])
        this.updateSelect([], [])
      }
    },
    renderClear(callback) {
      if (this.selectedRowKeys.length <= 0) return null
      return () => <a style="margin-left: 24px" onClick={() => {
        callback()
        this.clearSelected()
      }}>清空</a>
    },
    renderAlert() {
      const totalNodes = this.needTotalList.map(item => (
        <span style="margin-right: 12px">
          {item.title}总计 <a
          style="font-weight: 600">{item.customRender ? item.customRender(item.total) : item.total}</a>
        </span>
      ))
      const clearNode = typeof this.alert.clear === 'function' ? this.renderClear(this.clearSelected) : null
      return (
        <a-alert showIcon style="margin-bottom: 16px">
          <template slot="message">
            <span style="margin-right: 12px">已选择: <a style="font-weight: 600">{this.selectedRows.length}</a></span>
            {totalNodes}
            {clearNode}
          </template>
        </a-alert>
      )
    }
  }
}
</script>

<style scoped lang="less">
.table-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: 8px;
}
</style>
